import {FC, useEffect, useState} from 'react'
import {Eye} from 'lucide-react'
import {GET} from '../../../../services/api'

type TableAllocation = {
  allocationId: number
  allocatedAt: string
  occupiedAt?: string
  releasedAt?: string
  cleaningStartedAt?: string
  status: string
}

type TableInfo = {
  tableId: number
  tableNumber: string
  tableTypeId: number
  environmentId: number
  bookingEnvironmentId: number
  environmentName: string
  pluginspaceId: number
  pricePerSeat?: number
  pricePerEnvironment?: number
  pricePerTable?: number
  priceByGroup?: boolean
  seatsPerTable?: number
  customCapacity?: number
  capacity?: number
}

type Props = {
  tableId: number
  tableName: string
  environmentId: number
  isOpen: boolean
  onClose: () => void
}

export const TableAllocationHistoryModal: FC<Props> = ({
  tableId,
  tableName,
  environmentId,
  isOpen,
  onClose,
}) => {
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null)
  const [allocations, setAllocations] = useState<TableAllocation[]>([])
  const [loading, setLoading] = useState(false)
  const [statusCount, setStatusCount] = useState({
    allocated: 0,
    occupied: 0,
    cleaning: 0,
    released: 0,
    total: 0,
  })

  useEffect(() => {
    if (isOpen && tableId && environmentId) {
      loadAllocations()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tableId, environmentId])

  const loadAllocations = async () => {
    setLoading(true)
    const todayDate = new Date().toISOString().split('T')[0]
    console.log(
      '📂 loadAllocations chamado para tableId:',
      tableId,
      'environmentId:',
      environmentId,
      'date:',
      todayDate
    )
    try {
      const res = await GET(
        `/queueReservation/table-management/table/${tableId}/allocation-history?environmentId=${environmentId}&date=${todayDate}`
      )
      console.log('📦 Resposta da API allocation-history:', res)

      if (res?.success && res?.data) {
        console.log('✅ Dados carregados com sucesso')
        console.log('📊 Informações da mesa:', res.data.table)
        console.log('� Mesa - tableNumber:', res.data.table?.tableNumber)
        console.log('📍 Mesa - seatsPerTable:', res.data.table?.seatsPerTable)
        console.log('📍 Mesa - customCapacity:', res.data.table?.customCapacity)
        console.log('📍 Mesa - capacity:', res.data.table?.capacity)
        console.log('�📋 Alocações:', res.data.allocations)
        console.log('📈 Status count:', res.data.statusCount)

        setTableInfo(res.data.table)
        setAllocations(res.data.allocations)
        setStatusCount(res.data.statusCount)
      } else {
        console.log('⚠️ Nenhuma alocação encontrada ou success=false')
        setAllocations([])
        setStatusCount({allocated: 0, occupied: 0, cleaning: 0, released: 0, total: 0})
      }
    } catch (error) {
      console.error('❌ Erro ao carregar alocações:', error)
      setAllocations([])
      setStatusCount({allocated: 0, occupied: 0, cleaning: 0, released: 0, total: 0})
    } finally {
      setLoading(false)
    }
  }

  // const getStatusBadge = (status: string) => {
  //   const statusMap: {[key: string]: string} = {
  //     allocated: 'bg-info',
  //     occupied: 'bg-success',
  //     cleaning: 'bg-warning',
  //     released: 'bg-secondary',
  //   }
  //   return statusMap[status] || 'bg-light'
  // }

  if (!isOpen) return null

  return (
    <div
      className='modal fade show d-block'
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        className='modal-dialog modal-lg'
        style={{maxHeight: '90vh', display: 'flex', flexDirection: 'column'}}
      >
        <div
          className='modal-content'
          style={{display: 'flex', flexDirection: 'column', maxHeight: '90vh'}}
        >
          <div className='modal-header'>
            <h5 className='modal-title'>
              <Eye className='inline me-2' size={20} />
              Histórico de Alocações - Mesa {tableName}
            </h5>
            <button
              type='button'
              className='btn-close'
              onClick={onClose}
              aria-label='Fechar'
            ></button>
          </div>

          <div className='modal-body' style={{overflowY: 'auto', flex: '1'}}>
            {tableInfo && (
              <div className='alert alert-secondary mb-3'>
                <h6 className='mb-2'>📍 Informações da Mesa</h6>
                <small>
                  <strong>Mesa:</strong> {tableInfo.tableNumber} | <strong>Ambiente:</strong>{' '}
                  {tableInfo.environmentName} | <strong>Assentos:</strong>{' '}
                  {(() => {
                    const seats =
                      tableInfo.seatsPerTable || tableInfo.customCapacity || tableInfo.capacity
                    console.log('🔍 Exibindo assentos:', {
                      seatsPerTable: tableInfo.seatsPerTable,
                      customCapacity: tableInfo.customCapacity,
                      capacity: tableInfo.capacity,
                      final: seats,
                    })
                    return seats || '-'
                  })()}
                </small>
              </div>
            )}

            {/* Status Summary */}
            {statusCount.total > 0 && (
              <div className='row mb-3'>
                <div className='col-6 col-md-3 mb-2'>
                  <small className='text-muted'>Alocadas</small>
                  <div className='h5 mb-0'>{statusCount.allocated}</div>
                </div>
                <div className='col-6 col-md-3 mb-2'>
                  <small className='text-muted'>Ocupadas</small>
                  <div className='h5 mb-0'>{statusCount.occupied}</div>
                </div>
                <div className='col-6 col-md-3 mb-2'>
                  <small className='text-muted'>Limpeza</small>
                  <div className='h5 mb-0'>{statusCount.cleaning}</div>
                </div>
                <div className='col-6 col-md-3 mb-2'>
                  <small className='text-muted'>Total</small>
                  <div className='h5 mb-0'>{statusCount.total}</div>
                </div>
              </div>
            )}

            {loading ? (
              <div className='text-center py-4'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : allocations.length === 0 ? (
              <div className='alert alert-info mb-0'>
                <i className='bi bi-info-circle me-2'></i>
                Nenhuma alocação encontrada para esta mesa a partir de hoje.
              </div>
            ) : (
              <>
                <div
                  className='table-responsive'
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid #e4e6ef',
                    borderRadius: '0.425rem',
                  }}
                >
                  <table className='table table-sm table-hover mb-0'>
                    <thead className='table-light position-sticky' style={{top: 0}}>
                      <tr>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Alocada</th>
                        <th>Ocupada</th>
                        <th>Limpeza</th>
                        <th>Liberada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations.map((alloc) => (
                        <tr key={alloc.allocationId}>
                          <td>
                            <small>{alloc.allocationId}</small>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                alloc.status === 'occupied'
                                  ? 'badge-light-danger'
                                  : alloc.status === 'allocated'
                                  ? 'badge-light-warning'
                                  : alloc.status === 'cleaning'
                                  ? 'badge-light-info'
                                  : 'badge-light-secondary'
                              }`}
                            >
                              {alloc.status === 'occupied'
                                ? 'Ocupada'
                                : alloc.status === 'allocated'
                                ? 'Alocada'
                                : alloc.status === 'cleaning'
                                ? 'Limpeza'
                                : 'Liberada'}
                            </span>
                          </td>
                          <td>
                            <small>
                              {alloc.allocatedAt
                                ? new Date(alloc.allocatedAt).toLocaleString('pt-BR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <small>
                              {alloc.occupiedAt
                                ? new Date(alloc.occupiedAt).toLocaleString('pt-BR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <small>
                              {alloc.cleaningStartedAt
                                ? new Date(alloc.cleaningStartedAt).toLocaleString('pt-BR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <small>
                              {alloc.releasedAt
                                ? new Date(alloc.releasedAt).toLocaleString('pt-BR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '-'}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className='alert alert-warning mt-3 mb-0'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  <strong>Atenção:</strong> Esta mesa estando no status de alocada, ocupada ou em
                  limpeza, não poderá ser editada, excluída ou alterada sua situação de
                  ativa/inativa até que esteja no status de liberada ou vaga.
                </div>
              </>
            )}
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
